import React, {useState} from 'react';

import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import LegacyToggleGroup from 'ui-library/LegacyToggleGroup';
import Modal from 'ui-library/modals/Modal';

const FEATURE_DESCRIPTION =
  'must specify the field value on item creation - after creation, the value is uneditable.';

const UNEDITABLE_ICON = 'lock';
const EDITABLE_ICON = 'edit';

enum Editability {
  EDITABLE = 'EDITABLE',
  UNEDITABLE = 'UNEDITABLE',
}

function toEnum(isUneditable: boolean): Editability {
  return isUneditable ? Editability.UNEDITABLE : Editability.EDITABLE;
}

function fromEnum(editability: Editability): boolean {
  return editability === Editability.UNEDITABLE;
}

/**
 * Modal to configure field.isUneditable for the given field.
 */
export function SetUneditableFieldModal({
  field,
  onRequestSubmit,
  onRequestClose,
}: {
  field: SchemaField;
  onRequestSubmit: (value: {isUneditable: boolean}) => void;
  onRequestClose: () => void;
}): JSX.Element {
  // TODO(BNCH-56776): Remove `?? false` fallback.
  const [isUneditable, setIsUneditable] = useState<boolean>(field.isUneditable ?? false);
  return (
    <Modal
      medium={true}
      title={`Toggle editability of "${field.name}" field`}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit({isUneditable});
      }}
      buttons={[{text: 'Save', submit: true}]}
    >
      <p>By marking this field as uneditable, users {FEATURE_DESCRIPTION}</p>
      <LegacyToggleGroup<Editability>
        selection={toEnum(isUneditable)}
        onChange={(selection) => {
          setIsUneditable(fromEnum(selection));
        }}
        size={ButtonSize.SMALL}
        leftOption={{id: Editability.EDITABLE, buttonProps: {text: 'Editable', icon: EDITABLE_ICON}}}
        rightOption={{id: Editability.UNEDITABLE, buttonProps: {text: 'Uneditable', icon: UNEDITABLE_ICON}}}
      />
    </Modal>
  );
}

/**
 * Icon to indicate a schema field is uneditable with feature description on hover.
 */
export function UneditableFieldIcon({iconClassName}: {iconClassName?: string}): JSX.Element {
  return (
    <Popover title="Uneditable" renderContent={() => `You ${FEATURE_DESCRIPTION}`}>
      <BenchlingIcon icon={UNEDITABLE_ICON} className={iconClassName} />
    </Popover>
  );
}
